import { useId } from "react";
import { controlStyles } from "../../../utils/controlStyles";
import { Button } from "../../Button";
import { Tooltip } from "../../Tooltip";
import { DisabledTooltipTrigger } from "../../Tooltip/DisabledTooltipTrigger";

export interface TiledTimeSeriesLiveControl {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Healthy requires a delivered signal and current measurement evidence where available. */
  status: "live" | "connecting" | "reconnecting" | "stale" | "delayed" | "refresh-failed";
  disabledReason?: string | undefined;
  /** Optional cadence context owned by the consumer, shown while waiting for an update. */
  waitingDescription?: string | undefined;
}

const STATUS = {
  live: "Receiving updates",
  connecting: "Waiting for next update",
  reconnecting: "Reconnecting…",
  stale: "No recent updates",
  delayed: "Measurements delayed",
  "refresh-failed": "Update delayed",
};

const UNAVAILABLE_CLASSES = [
  "flex items-center justify-center whitespace-nowrap rounded-[var(--control-border-radius)] font-medium text-text-secondary opacity-50",
  controlStyles.sm.text,
  controlStyles.sm.height,
  controlStyles.sm.padding,
  controlStyles.sm.gap,
].join(" ");

/** Keeps the requested mode distinct from delivery and measurement status. */
export function LiveControl({
  label,
  checked,
  onChange,
  status,
  disabledReason,
  waitingDescription,
}: TiledTimeSeriesLiveControl) {
  const descriptionId = useId();
  const healthy = checked && status === "live";
  const waiting = checked && status === "connecting" && waitingDescription ? `. ${waitingDescription}` : "";
  const description = disabledReason || (checked ? `${STATUS[status]}${waiting}` : undefined);
  const face = (
    <>
      <span
        aria-hidden="true"
        data-live-indicator={healthy ? "live" : "neutral"}
        className={`size-1.5 shrink-0 rounded-full ${healthy ? "bg-error-base" : "bg-text-secondary"}`}
      />
      <span>{checked ? "Live" : "Static"}</span>
    </>
  );

  return (
    <div className="flex shrink-0 items-center">
      {disabledReason ? (
        <DisabledTooltipTrigger
          reason={disabledReason}
          aria-label={label}
          aria-pressed={checked}
          aria-describedby={descriptionId}
          className={UNAVAILABLE_CLASSES}
        >
          {face}
        </DisabledTooltipTrigger>
      ) : (
        <Tooltip content={description} isDisabled={!description}>
          <Button
            variant="ghost"
            size="sm"
            aria-label={label}
            aria-pressed={checked}
            {...(description && { "aria-describedby": descriptionId })}
            onPress={() => onChange(!checked)}
            className={checked ? "text-text-primary" : "text-text-secondary"}
          >
            {face}
          </Button>
        </Tooltip>
      )}
      <span id={descriptionId} role="status" aria-atomic="true" className="sr-only">
        {description}
      </span>
    </div>
  );
}
